import { FC } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

const AboutSection = styled.section`
  background-color: ${theme.secondary};
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: 5rem 2rem;
  }
`;

const AboutText = styled.div`
  h2 {
    color: ${theme.primaryDark};
    margin-bottom: 1.5rem;
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
  
  ul {
    list-style-position: inside;
    margin-bottom: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ProductsSection = styled.div`
  margin-top: 2rem;
  
  h3 {
    color: ${theme.primaryDark};
    margin-bottom: 1rem;
  }
  
  h4 {
    color: ${theme.primaryDark};
    margin-bottom: 0.5rem;
  }
  
  ul {
    list-style-type: none;
    
    li {
      padding-left: 1.5rem;
      position: relative;
      
      &:before {
        content: "•";
        position: absolute;
        left: 0;
        color: ${theme.primary};
      }
    }
  }
`;

const FeatureBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  background-color: ${theme.white};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h3 {
    color: ${theme.primaryDark};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  .icon {
    font-size: 2.5rem;
    color: ${theme.primary};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (min-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const UspSection = styled.div`
  background-color: ${theme.primaryLight};
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
  
  h3 {
    color: ${theme.primaryDark};
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const About: FC = () => {
  return (
    <AboutSection id="tietoa">
      <AboutContent>
        <AboutText>
          <h2>CE-merkitty tiilenpunainen graniittimurske Ruovedellä</h2>
          <p>
            Ruhalan Murske myy CE-merkittyä tiilenpunaista graniittimursketta Ruovedellä. Murskaamo sijaitsee 2 kilometrin 
            päässä kantatie 66:sta Ruhalassa, Pirkanmaalla. Korkealaatuiset murskeemme soveltuvat 
            erinomaisesti maa-, vesi- ja tierakentamisen sitomattomiin rakennekerroksiin, yleiseen 
            rakentamiseen, betonin kiviaineeksi sekä asfalttipäällysteisiin.
          </p>
          
          <ProductsSection>
            <h3>Tuotevalikoima - Kaikki CE-merkityt tuotteet</h3>
            
            <h4>Tiilenpunaiset murskeet</h4>
            <ul>
              <li>
                <strong>Murske KAM 0-16 mm (punainen):</strong> Erityisen kaunis tiilenpunainen murske, joka soveltuu erinomaisesti pihojen käytäville, puutarharakenteisiin ja muihin näkyviin kohteisiin. Tiilenpunainen sävy tuo lämpimän ja kutsuvan ilmeen pihoihin. Ihanteellinen valinta, kun halutaan luoda visuaalisesti vaikuttavia pintoja.
              </li>
            </ul>
            
            <h4>Puna-harmaat murskeet</h4>
            <ul>
              <li>
                <strong>Murske KAM 0-16 mm (puna-harmaa):</strong> Hyvä yleiskäyttöinen murske piharakenteisiin, kevyempiin 
                kantaviin kerroksiin ja sorateiden kunnossapitoon. Toimii myös asfaltin raaka-aineena. Puna-harmaa sävy antaa neutraalimman ilmeen.
              </li>
              <li>
                <strong>Murske KAM 0-32 mm (puna-harmaa):</strong> Käytetään kantaviin ja jakaviin kerroksiin, jotka vaativat 
                erityistä kantavuutta. Soveltuu tie- ja talonrakennuspohjaksi. Puna-harmaa sävy säilyttää maanläheisen ulkonäön.
              </li>
              <li>
                <strong>Murske KAM 0-56 mm (puna-harmaa):</strong> Tarkoitettu suurempaa kantavuutta vaativiin jakaviin kerroksiin, 
                raskaasti kuormitettujen teiden pohjiin ja rakennusten pohjarakenteisiin.
              </li>
            </ul>
            
            <h4>Harmaat murskeet</h4>
            <ul>
              <li>
                <strong>Murske KAM 0-8 mm (harmaa):</strong> Soveltuu erinomaisesti erilaisten pintojen ja pohjien tasaamiseen, 
                piharakentamiseen, kävely- ja pyöräteiden pohjiin sekä rakennusten sokkelin vierustäyttöön. Harmaa, klassinen väri.
              </li>
              <li>
                <strong>Murske KAM 0-90 mm (harmaa):</strong> Karkein murske, jota käytetään ensisijaisesti suodatinkerroksina 
                vaativissa rakennuskohteissa ja suuremmissa tierakenteissa.
              </li>
            </ul>
            
            <h4>Sepelit</h4>
            <ul>
              <li>
                <strong>Sepeli SEP 08-16 mm:</strong> Puhdas ja lajiteltu sepeli, joka sopii salaojituksiin, 
                puutarharakenteisiin ja betonin valmistukseen. Parantaa veden läpäisevyyttä.
              </li>
              <li>
                <strong>Sepeli SEP 16-32 mm:</strong> Karkeampi sepeli salaojiin, koristeeksi, betonivalujen pohjiin 
                ja paikkoihin, joissa tarvitaan hyvää vedenläpäisykykyä. Erinomainen rakennusten vierustäyttöön.
              </li>
            </ul>
            
            <p>Kaikki tuotteet soveltuvat maa- ja vesirakentamiseen sekä tierakenteisiin. CE-merkintä varmistaa tuotteiden laadun ja soveltuvuuden virallisiin rakennuskohteisiin.</p>
          </ProductsSection>
          
          <p>
            Asiakkaat voivat sopia toimituksesta tai noutaa tuotteet itse. Toimitus sovittavissa 1–2 viikon aikana; toimituksesta sovitaan tilauksen käsittelyn yhteydessä. Tiilenpunaiset murskeemme 
            sopivat erinomaisesti monentyyppisiin rakennusprojekteihin ja tuovat ainutlaatuista 
            väriä piharakentamiseen.
          </p>
          
          <UspSection>
            <h3>Laadukas tiilenpunainen graniittimurske</h3>
            <p>CE-merkitty tiilenpunainen murske täyttää kaikki rakentamisen laatuvaatimukset ja soveltuu sekä ammatti- että harrastajarakentajien käyttöön.</p>
            <p>Ainutlaatuinen tiilenpunainen sävy tuo lämmintä ilmettä pihoihin ja rakennuskohteisiin Pirkanmaalla ja lähialueilla.</p>
          </UspSection>
        </AboutText>
        
        <FeatureGrid>
          <FeatureBox>
            <div className="icon">🏗️</div>
            <h3>CE-merkityt tuotteet</h3>
            <p>Kaikki murskeemme ovat CE-merkittyjä, mikä takaa niiden soveltuvuuden rakentamisen eri tarpeisiin ja virallisiin kohteisiin.</p>
          </FeatureBox>
          
          <FeatureBox>
            <div className="icon">📍</div>
            <h3>Keskeinen sijainti</h3>
            <p>Murskaamo sijaitsee vain 2 km päässä kantatie 66:sta Ruhalassa, Pirkanmaalla, joten nouto on helppoa ja toimitukset nopeita.</p>
          </FeatureBox>
          
          <FeatureBox>
            <div className="icon">🚚</div>
            <h3>Toimitus tai nouto</h3>
            <p>Joustavat toimitusvaihtoehdot: voit noutaa tuotteet itse tai tilata kätevästi kotiinkuljetuksen haluamaasi osoitteeseen.</p>
          </FeatureBox>
          
          <FeatureBox>
            <div className="icon">🔍</div>
            <h3>Monipuolinen valikoima</h3>
            <p>Valikoimassa kahdeksan eri murske- ja sepelilajia erilaisiin rakennustarpeisiin: tierakentamiseen, pihoihin, betoni- ja asfalttitöihin.</p>
          </FeatureBox>
        </FeatureGrid>
      </AboutContent>
    </AboutSection>
  );
};

export default About; 